import { ConnectedRouter } from 'connected-react-router';
import { Redirect, Route, Switch } from 'react-router-dom';

import Page from 'components/Page';
import Protected from 'components/Protected';

import Layout from 'components/Layout';

import { useUnleashContext } from '@unleash/proxy-client-react';
import PageNoRecompose from 'components/Page/PageNoRecompose';
import ApplicationLayout from 'layouts/Application';
import BaseLayout from 'layouts/Base';
import CenteredLayout from 'layouts/Centered';
import NotAuthorizedLayout from 'layouts/NotAuthorized';
import SuccessPage from 'layouts/Pricing/components/SuccessPage';
import StaticSidebar from 'layouts/StaticSidebar';
import session from 'lib/session';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

type Translation = { [key: string]: string };

const Application = ({ history }: Props) => {
  const { t } = useTranslation('common');
  const translationValues = t('sidebar', {
    returnObjects: true,
  }) as Translation;
  const updateContext = useUnleashContext();

  const merchantId = useSelector((state: any) => state?.user?.merchantId);
  const tierId = useSelector((state: any) => state.user?.merchant?.tierId);
  const isAdmin = String(
    useSelector((state: any) => state.user?.data?.isAdmin === true)
  );
  const userId = session.userId;

  useEffect(() => {
    updateContext({
      properties: { organizationId: merchantId, tier: tierId, userId, isAdmin },
    });
  }, [merchantId, tierId, userId, isAdmin]);

  return (
    <ConnectedRouter history={history}>
      <BaseLayout>
        <Switch>
          <Route
            path="/downgrade-success"
            component={() => (
              <SuccessPage text="Your account will be downgraded within 24 hours" />
            )}
          />
          <Route
            path="/upgrade-success"
            component={() => (
              <SuccessPage text="Your new plan has been successfully upgraded. We’ve unlocked your new tools & features." />
            )}
          />
          <Page
            path="/onboard-shopify"
            component={import('pages/Onboarding/OnboardShopify')}
            title="Shopify"
          />
          {/* Old Sign in route */}
          {/* <Page
            path="/login"
            component={import('pages/Auth/SignIn')}
            title="Sign in to your Findify Dashboard"
          /> */}

          <Layout path="/auth" component={NotAuthorizedLayout}>
            <Page
              path="/cap-rejection/:url"
              component={import('pages/Auth/CapRejection')}
              title="Hallo!"
            />
            <Page
              exact
              path="/zero-products"
              component={import('pages/Auth/ZeroProducts')}
              title="Populate your store with products"
            />
            <Page
              exact
              path="/register"
              component={import('pages/Auth/Register')}
              title="Create Merchandising Cloud account"
            />
            <Page
              path="/register-success"
              component={import('pages/Auth/RegisterSuccess')}
              title="Check your email"
            />
            <Page
              path="/confirm-email"
              component={import('pages/Auth/ConfirmEmail')}
              title="Confirm Email"
            />
            <Page
              path="/set-password/:token?"
              component={import('pages/Auth/SetPassword')}
              title="Create password"
            />
            <Page
              path="/sign-in"
              component={import('pages/Auth/SignInWithMaropost')}
              title="Sign in to your Findify Dashboard"
            />
            <Page
              path="/forgot-password"
              component={import('pages/Auth/ForgotPassword')}
              title="Troubles logging in?"
            />
            <Page
              path="/forgot-password-success"
              component={import('pages/Auth/ForgotPasswordSuccess')}
              title="Forgot your password?"
            />
            <Page
              path="/create-password/:token"
              component={import('pages/Auth/CreatePassword')}
              title="Create new password"
            />
            <Page
              path="/kc/"
              render={require('pages/Auth/CreateKcSession').default}
            />
            <Page
              path="/:token"
              render={require('pages/Auth/CreateSession').default}
            />

            <Page
              path="/:token/:merchant"
              render={require('pages/Auth/CreateSession').default}
            />
          </Layout>

          <Layout
            path="/onboarding"
            component={ApplicationLayout}
            allow={(p) => !p.merchantId}
            redirect="/"
          >
            <Page
              path="/"
              title="Onboarding"
              component={() => import('pages/Onboarding')}
            />
          </Layout>

          <Protected allow={session.isAuthenticated}>
            <Layout
              component={ApplicationLayout}
              sidebar={StaticSidebar}
              allow={(p) => p.merchantId}
              redirect="/onboarding"
            >
              <Layout path="/" title="Dashboard">
                <Page
                  path="/"
                  exact
                  component={() => import('pages/Dashboard')}
                />
              </Layout>

              <Layout path="analytics" title={translationValues.analytics}>
                <Page
                  path="/snapshot"
                  component={() => import('pages/Analytics/Snapshot')}
                />
                <Page
                  path="/search"
                  component={() => import('pages/Analytics/Search')}
                />
                <Page
                  path="/collections"
                  component={() => import('pages/Analytics/Collections')}
                />
                <Page
                  path="/recommendations"
                  component={() => import('pages/Analytics/Recommendations')}
                />
                {/* <Redirect to="/snapshot" /> */}
              </Layout>

              <Layout path="solutions/search" title={translationValues.search}>
                <Layout path="/content" title="Content search">
                  <Page
                    path="/"
                    exact
                    component={() => import('pages/Solutions/Content/List')}
                  />
                  <Page
                    path="/create"
                    component={() => import('pages/Solutions/Content/Create')}
                  />
                  <Page
                    path="/edit/:id"
                    component={() => import('pages/Solutions/Content/Update')}
                  />
                  <Redirect to="/" />
                </Layout>

                <Layout path="/pinning">
                  <Page
                    path="/"
                    exact
                    component={() =>
                      import('pages/Solutions/Search/Pinning/List')
                    }
                  />
                  <Page
                    path="/edit/:id"
                    component={() =>
                      import('pages/Solutions/Search/Pinning/Update')
                    }
                  />
                  <Page
                    path="/create/:query?"
                    component={() =>
                      import('pages/Solutions/Search/Pinning/Create')
                    }
                  />
                </Layout>

                <Layout path="/blacklisting">
                  <Page
                    path="/suggestions"
                    component={() =>
                      import('pages/Solutions/Search/Blacklisting/Query')
                    }
                  />
                  <Page
                    path="/products"
                    exact
                    component={() =>
                      import(
                        'pages/Solutions/Search/Blacklisting/Products/List'
                      )
                    }
                  />
                  <Page
                    path="/products/:query"
                    component={() =>
                      import(
                        'pages/Solutions/Search/Blacklisting/Products/Edit'
                      )
                    }
                  />
                  <Redirect to="/suggestions" />
                </Layout>

                <Layout path="/banners">
                  <Page
                    path="/"
                    exact
                    component={() =>
                      import('pages/Solutions/Search/Banners/List')
                    }
                  />
                  <Page
                    path="/create"
                    component={() =>
                      import('pages/Solutions/Search/Banners/Create')
                    }
                  />
                  <Page
                    path="/edit/:id"
                    component={() =>
                      import('pages/Solutions/Search/Banners/Update')
                    }
                  />
                </Layout>

                <Layout path="/promos">
                  <Page
                    path="/"
                    exact
                    component={() =>
                      import('pages/Solutions/Search/Promos/List')
                    }
                  />
                  <Page
                    path="/create"
                    component={() =>
                      import('pages/Solutions/Search/Promos/Create')
                    }
                  />
                  <Page
                    path="/edit/:id"
                    component={() =>
                      import('pages/Solutions/Search/Promos/Update')
                    }
                  />
                </Layout>

                <Layout path="/rules">
                  <Page
                    path="/"
                    exact
                    component={() =>
                      import('pages/Solutions/Search/MerchandisingRules')
                    }
                  />
                  <Page
                    path="/create"
                    component={() =>
                      import('pages/Solutions/Search/Rules/Create')
                    }
                  />
                  <Page
                    path="/edit/:id"
                    component={() =>
                      import('pages/Solutions/Search/Rules/Update')
                    }
                  />
                </Layout>

                <Layout path="/synonyms">
                  {/* <Page
                    path="/manual"
                    component={() =>
                      import('pages/Solutions/Search/Synonyms/Manual')
                    }
                  /> */}
                  <Page
                    path="/"
                    component={() =>
                      import('pages/Solutions/Search/Synonyms/OneWay')
                    }
                  />
                  <Page
                    path="/suggested"
                    component={() =>
                      import('pages/Solutions/Search/Synonyms/Suggested')
                    }
                  />
                  <Redirect to="/manual" />
                </Layout>

                <Layout path="/preview">
                  <Page
                    path="/"
                    component={() => import('pages/Solutions/Search/Preview')}
                  />
                </Layout>

                <Page
                  path="/redirects"
                  component={() => import('pages/Solutions/Search/Redirects')}
                />

                <Redirect to="/pinning" />
              </Layout>

              <Layout
                path="solutions/recommendations"
                title={translationValues.recommendations}
              >
                <Layout path="/widgets">
                  <Page
                    path="/"
                    exact
                    component={() =>
                      import('pages/Solutions/Recommendations/Widgets/List')
                    }
                  />
                  <Page
                    path="/create"
                    component={() =>
                      import('pages/Solutions/Recommendations/Widgets/Create')
                    }
                  />
                  <Page
                    path="/edit/:id"
                    component={() =>
                      import('pages/Solutions/Recommendations/Widgets/Update')
                    }
                  />
                </Layout>
                <Redirect to="/widgets" />
              </Layout>

              <Layout
                path="solutions/smart-collections"
                title={translationValues.smart_collections}
              >
                <Layout path="/collections">
                  <Page
                    path="/"
                    exact
                    component={() =>
                      import(
                        'pages/Solutions/SmartCollections/Collections/List'
                      )
                    }
                  />
                  <Page
                    path="/create"
                    component={() =>
                      import(
                        'pages/Solutions/SmartCollections/Collections/Create'
                      )
                    }
                  />
                  <Page
                    path="/edit/:id"
                    component={() =>
                      import(
                        'pages/Solutions/SmartCollections/Collections/Update'
                      )
                    }
                  />
                </Layout>

                <Layout path="/banners">
                  <Page
                    path="/"
                    exact
                    component={() =>
                      import('pages/Solutions/SmartCollections/Banners/List')
                    }
                  />
                  <Page
                    path="/create"
                    component={() =>
                      import('pages/Solutions/SmartCollections/Banners/Create')
                    }
                  />
                  <Page
                    path="/edit/:id"
                    component={() =>
                      import('pages/Solutions/SmartCollections/Banners/Update')
                    }
                  />
                </Layout>

                <Layout path="/promos">
                  <Page
                    path="/"
                    exact
                    component={() =>
                      import('pages/Solutions/SmartCollections/Promos/List')
                    }
                  />
                  <Page
                    path="/create"
                    component={() =>
                      import('pages/Solutions/SmartCollections/Promos/Create')
                    }
                  />
                  <Page
                    path="/edit/:id"
                    component={() =>
                      import('pages/Solutions/SmartCollections/Promos/Update')
                    }
                  />
                </Layout>

                <Layout path="/pinning">
                  <Page
                    path="/"
                    exact
                    component={() =>
                      import('pages/Solutions/SmartCollections/Pinning/List')
                    }
                  />
                  <Page
                    path="/create"
                    component={() =>
                      import('pages/Solutions/SmartCollections/Pinning/Create')
                    }
                  />
                  <Page
                    path="/edit/:id"
                    component={() =>
                      import('pages/Solutions/SmartCollections/Pinning/Update')
                    }
                  />
                </Layout>

                <Layout path="/rules">
                  <Page
                    path="/"
                    exact
                    component={() =>
                      import(
                        'pages/Solutions/SmartCollections/MerchandisingRules'
                      )
                    }
                  />
                  <Page
                    path="/create"
                    component={() =>
                      import('pages/Solutions/SmartCollections/Rules/Create')
                    }
                  />
                  <Page
                    path="/edit/:id"
                    component={() =>
                      import('pages/Solutions/SmartCollections/Rules/Update')
                    }
                  />
                </Layout>

                <Redirect to="/collections" />
              </Layout>

              <Layout
                path="integrations"
                title="Integrations"
                component={CenteredLayout}
              >
                <Page
                  path="/"
                  exact
                  component={() => import('pages/Plugins/Review/List')}
                />
                <Page
                  path="/:provider"
                  component={() => import('pages/Plugins/Review/Provider')}
                />
              </Layout>

              <Layout path="settings" title="Sync, status and usage">
                <Redirect to="/integration" />
              </Layout>

              <Layout path="setup" title={translationValues.setup_title}>
                <PageNoRecompose
                  path="/primary/filters/edit/:id"
                  file={() => import('pages/Setup/Filters/Edit')}
                />

                <Page
                  path="/integration"
                  component={() => import('pages/Settings/Integration')}
                />

                <Layout path="/product-sync">
                  <Page
                    path="/overview"
                    component={() =>
                      import('pages/Settings/ProductSync/Overview')
                    }
                  />
                  <Redirect to="/overview" />
                </Layout>

                <Page
                  path="/advanced/frontend"
                  component={() => import('pages/Setup/Advanced')}
                />

                <Page
                  path="/advanced/general"
                  component={() =>
                    import('pages/Setup/NewAdvanced/General/index')
                  }
                />
                <Page
                  path="/advanced/search"
                  component={() => import('pages/Setup/NewAdvanced/Search')}
                />
                <Page
                  path="/advanced/autocomplete"
                  component={() =>
                    import('pages/Setup/NewAdvanced/Autocomplete')
                  }
                />
                <Page
                  path="/advanced/recommendations"
                  component={() =>
                    import('pages/Setup/NewAdvanced/Recommendations')
                  }
                />
                <Page
                  path="/advanced/platform-configuration"
                  exact
                  component={() =>
                    import(
                      'pages/Setup/NewAdvanced/PlatformConfiguration/Configuration'
                    )
                  }
                />
                <Page
                  path="/advanced/trend-score"
                  exact
                  component={() =>
                    import('pages/Setup/NewAdvanced/PopularityConfiguration')
                  }
                />
                <Page
                  path="/advanced/ip-blocking"
                  component={() => import('pages/Setup/IPBlocking')}
                />
                <Page
                  path="/primary/stickers"
                  exact
                  component={() => import('pages/Setup/Stickers/List')}
                />

                <Page
                  path="/primary/filters"
                  component={() => import('pages/Setup/Filters')}
                />
                <Page
                  path="/primary/color-mapping"
                  component={() => import('pages/Setup/ColorMapping')}
                />
                <Page
                  path="/primary/stock"
                  component={() => import('pages/Setup/Stock')}
                />
                <Page
                  path="/primary/translations"
                  component={() => import('pages/Setup/Translations')}
                />
                <Page
                  path="/integrations"
                  exact
                  component={() => import('pages/Plugins/Review/List')}
                />
                <Page
                  path="/integrations/:provider"
                  component={() => import('pages/Plugins/Review/Provider')}
                />
                <Redirect to="/integration" />
              </Layout>

              <Layout
                path="multimarket-settings"
                title={'Multimarket settings'}
              >
                <Page
                  path="/markets"
                  component={() => import('pages/Multimarket/Markets')}
                />
                <Page
                  path="/currencies"
                  component={() => import('pages/Multimarket/Currencies')}
                />
                <Page
                  path="/languages"
                  component={() => import('pages/Multimarket/Languages')}
                />
                <Page
                  path="/regions"
                  component={() => import('pages/Multimarket/Regions')}
                />
                <Page
                  path="/warehouses"
                  component={() => import('pages/Multimarket/Warehouses')}
                />
                <Redirect to="/markets" />
              </Layout>

              <Layout path="account" title={translationValues.account}>
                <Page
                  path="/billing"
                  component={() => import('pages/Account/Billing')}
                />
                <Page
                  path="/shopify-plan"
                  component={() => import('pages/Onboarding/OnboardShopify')}
                />
                <Page
                  path="/profile"
                  component={() => import('pages/Account/Profile')}
                />
                <Layout path="/users">
                  <Page
                    path="/"
                    exact
                    component={() => import('pages/Account/Users/List')}
                  />
                  <Page
                    path="/create"
                    component={() => import('pages/Account/Users/Create')}
                  />
                  <Redirect to="/" />
                </Layout>
              </Layout>

              <Layout path="admin" title={translationValues.admin_panel}>
                <Page
                  path="/config"
                  exact
                  component={() => import('pages/Admin/Config')}
                />
                <Page
                  path="/injections"
                  exact
                  component={() => import('pages/Admin/Injections')}
                />
                <Page
                  path="/css"
                  exact
                  component={() => import('pages/Admin/Css')}
                />
                <Page
                  path="/components"
                  exact
                  component={() => import('pages/Admin/Components')}
                />
                <Page
                  path="/update-plan"
                  exact
                  component={() => import('pages/Admin/UpdatePlan')}
                />
                <Page
                  path="/generate-shopify-link"
                  exact
                  component={() => import('pages/Admin/GenerateShopifyLink')}
                />
                <Page
                  path="/cancel-shopify-charge"
                  exact
                  component={() => import('pages/Admin/CancelShopifyCharge')}
                />
                <Page
                  path="/manage-shopify-metafields"
                  exact
                  component={() =>
                    import('pages/Admin/ManageShopifyMetafields')
                  }
                />
                <Page
                  path="/personalization"
                  component={() => import('pages/Admin/Personalization')}
                />
                <Page
                  path="/search-configuration"
                  component={() => import('pages/Admin/SearchConfig')}
                />
                <Page
                  path="/merchant-info"
                  component={() => import('pages/Admin/UpdateMerchantInfo')}
                />
                <Page
                  path="/api-config"
                  component={() => import('pages/Admin/ApiConfiguration')}
                />
                <Page
                  path="/update-merchant-tier"
                  exact
                  component={() => import('pages/Admin/UpdateMerchantTier')}
                />
                <Page
                  path="/merchant-tiers"
                  exact
                  component={() => import('pages/Admin/MerchantTiers')}
                />
                <Redirect to="/config" />
              </Layout>

              <Page
                path="/advanced/frontend"
                component={() => import('pages/Setup/Advanced')}
              />

              <Page
                path="/advanced/general"
                component={() =>
                  import('pages/Setup/NewAdvanced/General/index')
                }
              />
              <Page
                path="/advanced/search"
                component={() => import('pages/Setup/NewAdvanced/Search')}
              />
              <Page
                path="/advanced/autocomplete"
                component={() => import('pages/Setup/NewAdvanced/Autocomplete')}
              />
              <Page
                path="/advanced/recommendations"
                component={() =>
                  import('pages/Setup/NewAdvanced/Recommendations')
                }
              />
              <Page
                path="/advanced/ip-blocking"
                component={() => import('pages/Setup/IPBlocking')}
              />
              <Page
                path="/primary/stickers"
                exact
                component={() => import('pages/Setup/Stickers/List')}
              />

              <Page
                path="/primary/filters"
                component={() => import('pages/Setup/Filters')}
              />
              <Page
                path="/primary/color-mapping"
                component={() => import('pages/Setup/ColorMapping')}
              />
              <Page
                path="/primary/stock"
                component={() => import('pages/Setup/Stock')}
              />
              <Page
                path="/primary/translations"
                component={() => import('pages/Setup/Translations')}
              />
              <Page
                path="/integrations"
                exact
                component={() => import('pages/Plugins/Review/List')}
              />
              <Page
                path="/integrations/:provider"
                component={() => import('pages/Plugins/Review/Provider')}
              />
              <Redirect to="/integration" />
            </Layout>

            <Layout path="account" title="Account">
              <Page
                path="/billing"
                component={() => import('pages/Account/Billing')}
              />
              <Page
                path="/profile"
                component={() => import('pages/Account/Profile')}
              />
              <Redirect to="" />
            </Layout>
          </Protected>
        </Switch>
      </BaseLayout>
    </ConnectedRouter>
  );
};

export const Router: any = Application;

type Props = {
  history: any;
};
